import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { money } from "@/lib/format";
import { BudgetBar, PhonePlaceholders, EndingSoon } from "@/components/brand";
import { fileUrl } from "@/lib/api";

function reduced() {
  return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function srcset(poster) {
  const webp = (poster?.variants || []).filter((v) => v.format === "webp");
  return webp.map((v) => `${fileUrl(v.url)} ${v.w}w`).join(", ");
}

export default function HeroCarousel({ slides = [], onOpen }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = slides.length;
  const go = useCallback((i) => setIdx(((i % n) + n) % n), [n]);

  useEffect(() => {
    if (!n || paused || reduced()) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n, paused]);

  if (!n) return null;

  return (
    <div
      className="hero-box"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}
      style={{ position: "relative", borderRadius: 16, overflow: "hidden", width: "100%" }}
      data-testid="hero-carousel"
      aria-roledescription="carousel"
    >
      {slides.map((s, i) => (
        <Slide key={s.id} slide={s} active={i === idx} first={i === 0} onOpen={onOpen} />
      ))}

      {/* dots */}
      <div style={{ position: "absolute", bottom: 20, left: "var(--hero-pad)", zIndex: 4, display: "flex", gap: 6 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={() => go(i)} aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`}
            style={{ height: 6, width: i === idx ? 22 : 6, borderRadius: 999, border: "none", cursor: "pointer", background: i === idx ? "var(--accent-light)" : "rgba(255,255,255,.35)", transition: "width .3s" }} />
        ))}
      </div>
      {/* arrows */}
      <div style={{ position: "absolute", bottom: 20, right: "var(--hero-pad)", zIndex: 4, display: "flex", gap: 8 }}>
        <button className="focus-ring" onClick={() => go(idx - 1)} aria-label="Previous" data-testid="hero-prev" style={arrowStyle}><ChevronLeft size={16} /></button>
        <button className="focus-ring" onClick={() => go(idx + 1)} aria-label="Next" data-testid="hero-next" style={arrowStyle}><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

function Slide({ slide: s, active, first, onOpen }) {
  const poster = s.posters?.hero;
  const hasText = !!(poster && s.posters?.hero_has_text);
  const mobilePoster = s.posters?.hero_mobile;
  const enabled = s.platforms?.filter((p) => p.enabled) || [];
  const ss = srcset(poster);
  return (
    <div
      aria-hidden={!active}
      style={{ position: "absolute", inset: 0, opacity: active ? 1 : 0, transition: "opacity .5s ease", pointerEvents: active ? "auto" : "none", borderRadius: 16, overflow: "hidden", background: poster?.placeholder ? `url(${poster.placeholder}) center/cover no-repeat` : "var(--surface)" }}
    >
      {poster ? (
        <picture>
          {mobilePoster && <source media="(max-width: 767px)" srcSet={srcset(mobilePoster) || fileUrl(mobilePoster.url)} />}
          {ss && <source type="image/webp" srcSet={ss} sizes="(min-width: 1600px) 1600px, 100vw" />}
          <img
            src={fileUrl(poster.url)} srcSet={ss || undefined} sizes="(min-width: 1600px) 1600px, 100vw"
            alt={poster.alt || s.title} fetchpriority={first ? "high" : "auto"}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: poster.focal ? `${poster.focal.x * 100}% ${poster.focal.y * 100}%` : "center" }}
          />
        </picture>
      ) : (
        <div className="brand-gradient" style={{ position: "absolute", inset: 0 }} />
      )}
      <div className="hero-scrim" style={{ position: "absolute", inset: 0 }} aria-hidden="true" />

      {hasText ? (
        <div style={{ position: "absolute", left: "var(--hero-pad)", bottom: 72, width: "min(320px, 80%)", zIndex: 2 }}>
          <BudgetBar used={s.budget_used} total={s.budget_total} showLabel={false} />
          <div style={{ fontSize: 13, color: "var(--banner-sub)", marginTop: 6 }}>{money(s.paid_out)} of {money(s.budget_total)} paid out</div>
          <button className="btn-brand focus-ring" onClick={() => onOpen(s)} tabIndex={active ? 0 : -1} data-testid={`hero-join-${s.slug}`} style={{ marginTop: 14, padding: "11px 22px", fontSize: 14 }}>
            Join campaign
          </button>
        </div>
      ) : (
        <div style={{ position: "absolute", inset: 0, padding: "var(--hero-pad)", paddingBottom: 72, display: "flex", flexDirection: "column", justifyContent: "flex-end", zIndex: 2 }}>
          <div style={{ width: "min(45%, 640px)" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {s.ending_soon ? <EndingSoon /> : <span className="chip" style={{ color: "var(--banner-sub)" }}>{s.category}</span>}
            </div>
            <h1 style={{ fontSize: "clamp(28px, 3.2vw, 44px)", lineHeight: 1.12, margin: 0, color: "#fff" }}>{s.tagline || s.title}</h1>
            <p style={{ color: "var(--banner-sub)", marginTop: 12, fontSize: 15 }}>
              {s.brand_name} is paying {money(s.top_rate)} per 1K views on {enabled.map((p) => p.label).join(", ")}
            </p>
            <div style={{ maxWidth: 360, marginTop: 18 }}>
              <BudgetBar used={s.budget_used} total={s.budget_total} showLabel={false} />
              <div style={{ fontSize: 13, color: "var(--banner-sub)", marginTop: 6 }}>{money(s.paid_out)} of {money(s.budget_total)} paid out</div>
            </div>
            <button className="btn-brand focus-ring" onClick={() => onOpen(s)} tabIndex={active ? 0 : -1} data-testid={`hero-join-${s.slug}`} style={{ marginTop: 20, padding: "11px 22px", width: "fit-content", fontSize: 14 }}>
              Join campaign
            </button>
          </div>
        </div>
      )}

      {!poster && (
        <div style={{ position: "absolute", right: "var(--hero-pad)", top: "50%", transform: "translateY(-50%)", zIndex: 2 }} className="hidden md:block" aria-hidden="true">
          <PhonePlaceholders />
        </div>
      )}
    </div>
  );
}

const arrowStyle = { width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,.08)", border: "1px solid var(--border-strong)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)" };
